import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FormField, HpButton } from '@hp/ui';
import { trpcClient } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';

interface LocationPickerProps {
  address: string;
  lat: string;
  lng: string;
  onChange: (lat: string, lng: string) => void;
}

/** Lat/lng inputs + Nominatim lookup of the address + OSM embed preview. */
export function LocationPicker({ address, lat, lng, onChange }: LocationPickerProps) {
  const { t } = useTranslation(['projects', 'common']);
  // A lookup the user triggers explicitly — modelled as a mutation so results appear on demand only.
  const geocode = useMutation({ mutationFn: (q: string) => trpcClient.projects.geocode.query({ q }) });

  const la = Number(lat);
  const ln = Number(lng);
  const hasPoint = lat.trim() !== '' && lng.trim() !== '' && Number.isFinite(la) && Number.isFinite(ln);

  return (
    <fieldset className="bo-fieldset">
      <legend>{t('location.legend')}</legend>
      <div className="bo-inline-form">
        <FormField label={t('location.lat')} className="bo-field--narrow">
          <input value={lat} onChange={(e) => onChange(e.target.value, lng)} />
        </FormField>
        <FormField label={t('location.lng')} className="bo-field--narrow">
          <input value={lng} onChange={(e) => onChange(lat, e.target.value)} />
        </FormField>
        <div className="bo-inline-form__submit bo-row">
          <HpButton
            variant="secondary"
            icon="pin"
            busy={geocode.isPending}
            disabled={address.trim().length < 3}
            onClick={() => geocode.mutate(address)}
            title={t('location.lookupTitle')}
          >
            {geocode.isPending ? t('location.searching') : t('location.find')}
          </HpButton>
          {hasPoint && (
            <a
              href={`https://www.openstreetmap.org/?mlat=${la}&mlon=${ln}#map=17/${la}/${ln}`}
              target="_blank"
              rel="noreferrer"
              className="hp-detail-action hp-detail-action--text"
            >
              {t('location.openMap')}
            </a>
          )}
        </div>
      </div>
      <ErrorAlert error={geocode.error} />
      {geocode.data && geocode.data.length === 0 && <p className="bo-muted">{t('location.noMatches')}</p>}
      {geocode.data && geocode.data.length > 0 && (
        <ul className="bo-picklist">
          {geocode.data.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => {
                  onChange(String(r.lat), String(r.lng));
                  geocode.reset();
                }}
              >
                <span>{r.displayName}</span>
                <span className="bo-mono bo-muted">
                  {r.lat.toFixed(5)}, {r.lng.toFixed(5)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {hasPoint && (
        <iframe title={t('location.mapTitle')} className="bo-map" src={osmEmbedUrl(la, ln)} loading="lazy" />
      )}
    </fieldset>
  );
}

function osmEmbedUrl(lat: number, lng: number): string {
  const bbox = [lng - 0.005, lat - 0.0025, lng + 0.005, lat + 0.0025].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat}%2C${lng}`;
}
